import { useState } from "react";
import { Pencil, Plus } from "lucide-react";
import {
  CanAccess,
  useDataProvider,
  useGetList,
  useNotify,
  useTranslate,
} from "ra-core";
import { Navigate } from "react-router";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useIsMobile } from "@/hooks/use-mobile";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";
import type { LeadSource } from "../types";
import {
  buildLeadSourcePayload,
  LEAD_SOURCE_NAME_MAX_LENGTH,
  LEAD_SOURCE_PALETTE,
  orderLeadSourcesForList,
  toLeadSourceErrorMessage,
  validateLeadSourceForm,
  type LeadSourceFormErrors,
  type LeadSourceFormValues,
} from "./leadSourceForm";

const DEFAULT_COLOR = LEAD_SOURCE_PALETTE[0].value;

/**
 * Almikia Epic E2b (TASK-022): the direccion-only "Orígenes de leads"
 * screen, reached from Settings. Gated by `CanAccess` on `lead_sources`/`edit`
 * (direccion only; RLS and the provider guard are the real authority). A
 * source is never deleted: "Desactivar" hides it from new-lead pickers while
 * historical leads keep it.
 */
export const LeadSourceList = () => (
  <CanAccess
    resource="lead_sources"
    action="edit"
    accessDenied={<Navigate to="/" replace />}
  >
    <LeadSourceListContent />
  </CanAccess>
);

LeadSourceList.path = "/lead_sources";

const LeadSourceListContent = () => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const isMobile = useIsMobile();

  const { data, isPending, error, refetch } = useGetList<LeadSource>(
    "lead_sources",
    {
      pagination: { page: 1, perPage: 100 },
      sort: { field: "name", order: "ASC" },
    },
  );
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<LeadSource["id"] | null>(null);
  const [toggleTarget, setToggleTarget] = useState<LeadSource | null>(null);
  const [isToggling, setIsToggling] = useState(false);

  const title = translate("crm.leadSources.page_title");

  const handleSave = async (
    values: LeadSourceFormValues,
    source?: LeadSource,
  ) => {
    const payload = buildLeadSourcePayload(values);
    if (source) {
      await dataProvider.update("lead_sources", {
        id: source.id,
        data: payload,
        previousData: source,
      });
      setEditingId(null);
      notify("crm.leadSources.updated_notice");
    } else {
      await dataProvider.create("lead_sources", { data: payload });
      setIsAdding(false);
      notify("crm.leadSources.created_notice");
    }
    refetch();
  };

  const handleToggleConfirm = async () => {
    if (!toggleTarget) return;
    setIsToggling(true);
    try {
      await dataProvider.update("lead_sources", {
        id: toggleTarget.id,
        data: { active: !toggleTarget.active },
        previousData: toggleTarget,
      });
      notify(
        toggleTarget.active
          ? "crm.leadSources.deactivated_notice"
          : "crm.leadSources.reactivated_notice",
      );
      refetch();
    } catch (writeError) {
      notify(toLeadSourceErrorMessage(writeError), { type: "error" });
    } finally {
      setIsToggling(false);
      setToggleTarget(null);
    }
  };

  let body;
  if (error) {
    body = <ListErrorState onRetry={() => refetch()} />;
  } else if (isPending || !data) {
    body = <ListCardsSkeleton rows={3} />;
  } else {
    const ordered = orderLeadSourcesForList(data);
    body = (
      <>
        {isAdding ? (
          <LeadSourceForm
            existing={ordered}
            onSave={(values) => handleSave(values)}
            onCancel={() => setIsAdding(false)}
          />
        ) : null}
        {ordered.length === 0 ? (
          <ListEmptyState
            title={translate("crm.leadSources.empty")}
            description={translate("crm.leadSources.empty_description")}
            action={
              // The screen itself is direccion-only (CanAccess on edit), so
              // every viewer here can create a source.
              isAdding ? undefined : (
                <Button
                  type="button"
                  className="min-h-11"
                  onClick={() => setIsAdding(true)}
                >
                  {translate("crm.leadSources.add_action")}
                </Button>
              )
            }
          />
        ) : (
          <div className="divide-y rounded-md border">
            {ordered.map((source) =>
              editingId === source.id ? (
                <div key={source.id} className="p-3">
                  <LeadSourceForm
                    existing={ordered}
                    source={source}
                    onSave={(values) => handleSave(values, source)}
                    onCancel={() => setEditingId(null)}
                  />
                </div>
              ) : (
                <div
                  key={source.id}
                  data-testid="lead-source-row"
                  className="flex flex-wrap items-center gap-3 p-3"
                >
                  <span
                    aria-hidden="true"
                    className="size-4 shrink-0 rounded-full"
                    style={{ backgroundColor: source.color ?? DEFAULT_COLOR }}
                  />
                  <div className="flex flex-1 min-w-0 flex-wrap items-center gap-2">
                    <span className="font-medium truncate">{source.name}</span>
                    {!source.active ? (
                      <StatusPill
                        tone="not-ok"
                        text={translate("crm.leadSources.inactive_marker")}
                      />
                    ) : null}
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="min-h-11"
                      onClick={() => setEditingId(source.id)}
                    >
                      <Pencil className="size-4 mr-1" />
                      {translate("crm.leadSources.edit_action")}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={source.active ? "destructive" : "default"}
                      className="min-h-11"
                      onClick={() => setToggleTarget(source)}
                    >
                      {source.active
                        ? translate("crm.leadSources.deactivate_action")
                        : translate("crm.leadSources.reactivate_action")}
                    </Button>
                  </div>
                </div>
              ),
            )}
          </div>
        )}
      </>
    );
  }

  const addButton = !isAdding ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="min-h-11"
      onClick={() => setIsAdding(true)}
    >
      <Plus className="size-4 mr-1" />
      {translate("crm.leadSources.add_action")}
    </Button>
  ) : null;

  const content = (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold">{title}</h2>
          {addButton}
        </div>
        {body}
      </CardContent>
      <Confirm
        isOpen={toggleTarget != null}
        loading={isToggling}
        title={
          toggleTarget?.active
            ? "crm.leadSources.confirm_deactivate_title"
            : "crm.leadSources.confirm_reactivate_title"
        }
        content={
          toggleTarget?.active
            ? "crm.leadSources.confirm_deactivate_body"
            : "crm.leadSources.confirm_reactivate_body"
        }
        confirm={
          toggleTarget?.active
            ? "crm.leadSources.deactivate_action"
            : "crm.leadSources.reactivate_action"
        }
        confirmColor={toggleTarget?.active ? "warning" : "primary"}
        onConfirm={handleToggleConfirm}
        onClose={() => setToggleTarget(null)}
      />
    </Card>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">{title}</h1>
          </div>
        </MobileHeader>
        <MobileContent>{content}</MobileContent>
      </>
    );
  }
  return (
    <div className="mx-auto max-w-2xl p-4 pb-8">
      <h1 className="text-2xl font-semibold mb-4">{title}</h1>
      {content}
    </div>
  );
};

/** Shared create/edit form: name, palette color, Meta mapping, "Activo" toggle. */
const LeadSourceForm = ({
  existing,
  source,
  onSave,
  onCancel,
}: {
  existing: readonly LeadSource[];
  source?: LeadSource;
  onSave: (values: LeadSourceFormValues) => Promise<void>;
  onCancel: () => void;
}) => {
  const translate = useTranslate();
  const idPrefix = `lead-source-${source?.id ?? "new"}`;
  const [values, setValues] = useState<LeadSourceFormValues>({
    name: source?.name ?? "",
    color: source?.color ?? DEFAULT_COLOR,
    meta_mapping: source?.meta_mapping ?? "",
    active: source?.active ?? true,
  });
  const [errors, setErrors] = useState<LeadSourceFormErrors>({});
  const [saveError, setSaveError] = useState<string | undefined>();
  const [isSaving, setIsSaving] = useState(false);

  const swatches = LEAD_SOURCE_PALETTE.some(
    (swatch) => swatch.value.toLowerCase() === values.color.toLowerCase(),
  )
    ? LEAD_SOURCE_PALETTE
    : [...LEAD_SOURCE_PALETTE, { value: values.color, label: values.color }];

  const handleSave = async () => {
    const found = validateLeadSourceForm(values, existing, source?.id);
    setErrors(found);
    setSaveError(undefined);
    if (found.name || found.color) return;
    setIsSaving(true);
    try {
      await onSave(values);
    } catch (error) {
      setSaveError(toLeadSourceErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-3 rounded-md border p-3 bg-muted/30">
      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-name`}>
          {translate("resources.lead_sources.fields.name")}
        </Label>
        <Input
          id={`${idPrefix}-name`}
          value={values.name}
          maxLength={LEAD_SOURCE_NAME_MAX_LENGTH + 20}
          disabled={isSaving}
          aria-invalid={errors.name != null}
          onChange={(event) =>
            setValues({ ...values, name: event.target.value })
          }
        />
        {errors.name ? (
          <p className="text-sm text-destructive">{errors.name}</p>
        ) : null}
      </div>

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium">
          {translate("resources.lead_sources.fields.color")}
        </legend>
        <div className="flex flex-wrap gap-2">
          {swatches.map((swatch) => {
            const isSelected =
              swatch.value.toLowerCase() === values.color.toLowerCase();
            return (
              <button
                key={swatch.value}
                type="button"
                aria-label={swatch.label}
                aria-pressed={isSelected}
                disabled={isSaving}
                onClick={() => setValues({ ...values, color: swatch.value })}
                className={`size-11 rounded-full border-2 ${
                  isSelected ? "border-foreground" : "border-transparent"
                }`}
                style={{ backgroundColor: swatch.value }}
              />
            );
          })}
        </div>
        {errors.color ? (
          <p className="text-sm text-destructive">{errors.color}</p>
        ) : null}
      </fieldset>

      <div className="flex flex-col gap-2">
        <Label htmlFor={`${idPrefix}-mapping`}>
          {translate("resources.lead_sources.fields.meta_mapping")}
        </Label>
        <Input
          id={`${idPrefix}-mapping`}
          value={values.meta_mapping}
          disabled={isSaving}
          aria-describedby={`${idPrefix}-mapping-help`}
          onChange={(event) =>
            setValues({ ...values, meta_mapping: event.target.value })
          }
        />
        <p
          id={`${idPrefix}-mapping-help`}
          className="text-sm text-muted-foreground"
        >
          {translate("crm.metaLeads.mapping_format_help")}
        </p>
      </div>

      <div className="flex items-center gap-2">
        <Switch
          id={`${idPrefix}-active`}
          checked={values.active}
          disabled={isSaving}
          onCheckedChange={(checked) =>
            setValues({ ...values, active: checked })
          }
        />
        <Label htmlFor={`${idPrefix}-active`}>
          {translate("resources.lead_sources.fields.active")}
        </Label>
      </div>

      {saveError ? (
        <p className="text-sm text-destructive">{saveError}</p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-11"
          disabled={isSaving}
          onClick={onCancel}
        >
          {translate("ra.action.cancel")}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="gradient"
          className="min-h-11"
          disabled={isSaving}
          onClick={handleSave}
        >
          {translate("ra.action.save")}
        </Button>
      </div>
    </div>
  );
};
